import {
  useCallback,
  useMemo,
  useState,
  type ComponentType,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  DeleteModal,
  Modal,
  Table,
} from "@telluscompany/design-system";
import { PlusCircle } from "lucide-react";

import { useLocalization } from "@i18n";
import { useUpdateTableUserPreferences } from "@shared/hooks";
import { Gs3TablePaginator } from "@shared/components";
import { useUserContext } from "@shared/contexts";
import {
  EMPTY_PAGINATION_META,
  parsePageSize,
} from "@shared/constants/pagination.constants";
import { serializeToSearchParams } from "@shared/hooks";

import { useAdminStructureReservoirManagementsQuery } from "../../api/structures/structures.queries";
import {
  useCreateAdminStructureReservoirManagementMutation,
  useDeleteAdminStructureReservoirManagementMutation,
  usePatchAdminStructureReservoirManagementMutation,
} from "../../api/structures/structures.mutations";
import type {
  AdminStructureReservoirManagementFilters,
  AdminStructureReservoirManagementListItem,
} from "../../api/structures/structures.types";
import type { AdminStructureReservoirManagementForm as ReservoirManagementFormValues } from "../../schemas";
import { AdminStructureReservoirManagementFilter } from "./AdminStructureReservoirManagementFilter";
import { AdminStructureReservoirManagementActionMenu } from "./AdminStructureReservoirManagementActionMenu";
import { AdminStructureReservoirManagementForm } from "./AdminStructureReservoirManagementForm";

const RESERVOIR_MANAGEMENT_PAGE_PARAM = "reservoirManagementPage";
const RESERVOIR_MANAGEMENT_LIMIT_PARAM = "reservoirManagementLimit";
const RESERVOIR_MANAGEMENT_TABLE_ID = "admin-structure-reservoir-management-table";
const DEFAULT_PAGE = 1;

type ReservoirManagementTableSort = {
  key: string;
  direction: string;
};

const RESERVOIR_MANAGEMENT_COLUMN_TO_API_SORT: Record<string, string> = {
  recordedOnDisplay: "recordedOn",
  occupiedArea: "occupiedArea",
  totalVolume: "totalVolume",
  totalOccupiedVolume: "totalOccupiedVolume",
  availableVolume: "availableVolume",
  rejectOccupiedVolume: "rejectOccupiedVolume",
  waterOccupiedVolume: "waterOccupiedVolume",
  waterLevel: "waterLevel",
};

const mapReservoirManagementSortToApi = (
  sort: ReservoirManagementTableSort | null,
): Pick<AdminStructureReservoirManagementFilters, "sortBy" | "sortOrder"> => {
  if (!sort) return {};

  const sortBy = RESERVOIR_MANAGEMENT_COLUMN_TO_API_SORT[sort.key];
  if (!sortBy) return {};

  return {
    sortBy,
    sortOrder: sort.direction as "asc" | "desc",
  };
};

const formatRecordedOn = (iso: string): string => {
  if (!iso) return "—";
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return iso;
  return `${day}/${month}/${year}`;
};

const emptyFormValues = (): ReservoirManagementFormValues => ({
  occupiedArea: "",
  totalVolume: "",
  totalOccupiedVolume: "",
  availableVolume: "",
  rejectOccupiedVolume: "",
  waterOccupiedVolume: "",
  waterLevel: "",
  recordedOn: "",
});

const toFormValues = (
  row: AdminStructureReservoirManagementListItem,
): ReservoirManagementFormValues => ({
  occupiedArea: row.occupiedArea,
  totalVolume: row.totalVolume,
  totalOccupiedVolume: row.totalOccupiedVolume,
  availableVolume: row.availableVolume,
  rejectOccupiedVolume: row.rejectOccupiedVolume,
  waterOccupiedVolume: row.waterOccupiedVolume,
  waterLevel: row.waterLevel,
  recordedOn: row.recordedOn,
});

export type AdminStructureReservoirManagementTabProps = {
  complexId: string;
  structureId: string;
  canWrite: boolean;
};

const AdminStructureReservoirManagementTab = ({
  complexId,
  structureId,
  canWrite,
}: AdminStructureReservoirManagementTabProps) => {
  const { translate } = useLocalization();
  const { user: me } = useUserContext();
  const updateTableUserPreferences = useUpdateTableUserPreferences(me?.userPreferences);
  const [searchParams, setSearchParams] = useSearchParams();

  const [appliedFilters, setAppliedFilters] =
    useState<AdminStructureReservoirManagementFilters>({});
  const [tableSort, setTableSort] =
    useState<ReservoirManagementTableSort | null>(null);
  const page = Math.max(
    1,
    Number(searchParams.get(RESERVOIR_MANAGEMENT_PAGE_PARAM) ?? DEFAULT_PAGE),
  );
  const limit = parsePageSize(
    searchParams.get(RESERVOIR_MANAGEMENT_LIMIT_PARAM),
  );

  const queryFilters = useMemo(
    () => ({
      ...appliedFilters,
      page,
      limit,
      ...mapReservoirManagementSortToApi(tableSort),
    }),
    [appliedFilters, limit, page, tableSort],
  );

  const { data } = useAdminStructureReservoirManagementsQuery(
    complexId,
    structureId,
    queryFilters,
  );
  const createMutation = useCreateAdminStructureReservoirManagementMutation();
  const patchMutation = usePatchAdminStructureReservoirManagementMutation();
  const deleteMutation = useDeleteAdminStructureReservoirManagementMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] =
    useState<AdminStructureReservoirManagementListItem | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<AdminStructureReservoirManagementListItem | null>(null);

  const resetReservoirManagementPage = useCallback(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set(RESERVOIR_MANAGEMENT_PAGE_PARAM, String(DEFAULT_PAGE));
      return next;
    });
  }, [setSearchParams]);

  const setReservoirManagementPage = useCallback(
    (nextPage: number) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set(RESERVOIR_MANAGEMENT_PAGE_PARAM, String(nextPage));
        return next;
      });
    },
    [setSearchParams],
  );

  const setReservoirManagementLimit = useCallback(
    (nextLimit: number) => {
      setSearchParams((current) =>
        serializeToSearchParams(
          {
            [RESERVOIR_MANAGEMENT_PAGE_PARAM]: DEFAULT_PAGE,
            [RESERVOIR_MANAGEMENT_LIMIT_PARAM]: nextLimit,
          },
          current,
        ),
      );
    },
    [setSearchParams],
  );

  const handleSelectedSortKey = useCallback<
    Dispatch<SetStateAction<ReservoirManagementTableSort>>
  >(
    (value) => {
      setTableSort((current) => {
        const next =
          typeof value === "function"
            ? value(current ?? { key: "", direction: "asc" })
            : value;
        return next;
      });
      resetReservoirManagementPage();
    },
    [resetReservoirManagementPage],
  );

  const openCreateModal = () => {
    setEditingRow(null);
    setModalOpen(true);
  };

  const openEditModal = useCallback(
    (row: AdminStructureReservoirManagementListItem) => {
      setEditingRow(row);
      setModalOpen(true);
    },
    [],
  );

  const closeModal = () => {
    setModalOpen(false);
    setEditingRow(null);
  };

  const onSubmit = async (values: ReservoirManagementFormValues) => {
    if (editingRow) {
      await patchMutation.mutateAsync({
        complexId,
        structureId,
        reservoirManagementId: editingRow.id,
        payload: values,
      });
    } else {
      await createMutation.mutateAsync({
        complexId,
        structureId,
        payload: values,
      });
    }
    closeModal();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteMutation.mutateAsync({
      complexId,
      structureId,
      reservoirManagementId: deleteTarget.id,
    });
    setDeleteTarget(null);
  };

  const handleFilter = (filters: AdminStructureReservoirManagementFilters) => {
    setAppliedFilters(filters);
    resetReservoirManagementPage();
  };

  const handleClearFilter = () => {
    setAppliedFilters({});
    resetReservoirManagementPage();
  };

  const isSubmitting = createMutation.isPending || patchMutation.isPending;

  const tableData = useMemo(
    () =>
      (data?.data ?? []).map((item) => ({
        ...item,
        recordedOnDisplay: formatRecordedOn(item.recordedOn),
      })),
    [data?.data],
  );

  const createActionMenu = useCallback(
    (rowData: { rowData: AdminStructureReservoirManagementListItem }) => (
      <AdminStructureReservoirManagementActionMenu
        row={rowData.rowData}
        onEdit={openEditModal}
        onDelete={setDeleteTarget}
      />
    ),
    [openEditModal],
  );

  const modalFormKey = editingRow?.id ?? "create";
  const modalDefaultValues = editingRow
    ? toFormValues(editingRow)
    : emptyFormValues();

  const tableColumns = useMemo(
    () => [
      {
        key: "recordedOnDisplay",
        label: translate("admin.structures.reservoirManagement.label.recordedOn"),
        minWidth: "120px",
        sortType: "date" as const,
      },
      {
        key: "occupiedArea",
        label: translate(
          "admin.structures.reservoirManagement.label.occupiedAreaShort",
        ),
        minWidth: "140px",
        sortType: "number" as const,
      },
      {
        key: "totalVolume",
        label: translate(
          "admin.structures.reservoirManagement.label.totalVolumeShort",
        ),
        minWidth: "120px",
        sortType: "number" as const,
      },
      {
        key: "totalOccupiedVolume",
        label: translate(
          "admin.structures.reservoirManagement.label.totalOccupiedVolumeShort",
        ),
        minWidth: "160px",
        sortType: "number" as const,
      },
      {
        key: "availableVolume",
        label: translate(
          "admin.structures.reservoirManagement.label.availableVolumeShort",
        ),
        minWidth: "140px",
        sortType: "number" as const,
      },
      {
        key: "rejectOccupiedVolume",
        label: translate(
          "admin.structures.reservoirManagement.label.rejectOccupiedVolumeShort",
        ),
        minWidth: "160px",
        sortType: "number" as const,
      },
      {
        key: "waterOccupiedVolume",
        label: translate(
          "admin.structures.reservoirManagement.label.waterOccupiedVolumeShort",
        ),
        minWidth: "180px",
        sortType: "number" as const,
      },
      {
        key: "waterLevel",
        label: translate("admin.structures.reservoirManagement.label.waterLevel"),
        minWidth: "120px",
        sortType: "number" as const,
      },
    ],
    [translate],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="admin-structure-reservoir-management-table">
        <Table<AdminStructureReservoirManagementListItem>
          userId={me?.id ?? ""}
          tableId={RESERVOIR_MANAGEMENT_TABLE_ID}
          usersPreferencesFromServer={me?.userPreferences}
          updateUsersPreferencesOnServer={updateTableUserPreferences}
          useSearchParamsHook={useSearchParams}
          setSelectedSortKey={handleSelectedSortKey}
          draggable
          enableTableColumnsSelector
          TableActionMenu={
            canWrite ? (createActionMenu as ComponentType) : undefined
          }
          TableButtons={
            canWrite ? (
              <Button
                size="small"
                variant="primary"
                onClick={openCreateModal}
                iconLeft={<PlusCircle />}
              >
                {translate("admin.structures.reservoirManagement.button.register")}
              </Button>
            ) : undefined
          }
          TableFilter={() => (
            <AdminStructureReservoirManagementFilter
              onFilter={handleFilter}
              onClear={handleClearFilter}
              initialFilters={{
                recordedOn: appliedFilters.recordedOn,
              }}
            />
          )}
          TablePaginator={
            <div data-testid="reservoir-management-paginator">
              <Gs3TablePaginator
                meta={data?.meta ?? EMPTY_PAGINATION_META}
                onPageChange={setReservoirManagementPage}
                onLimitChange={setReservoirManagementLimit}
                useSearchParamsHook={useSearchParams}
              />
            </div>
          }
          filterButtonText={translate(
            "admin.structures.reservoirManagement.table.filterButtonText",
          )}
          handleClickRow={() => {}}
          tableColumns={tableColumns}
          tableData={
            tableData as { [key: string]: unknown; color?: string }[]
          }
        />
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={translate(
          editingRow
            ? "admin.structures.reservoirManagement.modal.editTitle"
            : "admin.structures.reservoirManagement.modal.createTitle",
        )}
        content={
          <AdminStructureReservoirManagementForm
            key={modalFormKey}
            defaultValues={modalDefaultValues}
            isSubmitting={isSubmitting}
            onSubmit={onSubmit}
            onCancel={closeModal}
          />
        }
      />

      <DeleteModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isDeleting={deleteMutation.isPending}
        itemDescription={deleteTarget?.recordedOn}
        title={translate("admin.structures.reservoirManagement.modal.deleteTitle")}
        message={translate(
          "admin.structures.reservoirManagement.modal.deleteMessage",
        )}
        alertMessage={translate("common.deleteModalAlertMessage")}
        confirmButtonText={translate(
          "admin.structures.reservoirManagement.button.confirm",
        )}
        cancelButtonText={translate("common.cancel")}
        deletingButtonText={translate("common.deleting")}
      />
    </div>
  );
};

export default AdminStructureReservoirManagementTab;
